import { GroupingDirective, ListStore } from '@list-store';
import { MergeEventView } from '@mark43/cad-api';
import { action, computed, makeObservable, observable } from 'mobx';
import { TileEvent } from '../../../../common/types';
import { baseGroupingComparator } from '../../../../common/helpers/list-store';
import { isNotNullOrUndefined } from '../../../../queues/utils/is-not-null-or-undefined';
import { logWarn } from '../../../../monitoring/log';

export const MergeEventGroupEnum = {
    ALREADY_MERGED: {
        name: 'ALREADY_MERGED',
        displayName: 'Already Merged',
        index: 0,
    },
    TO_MERGE: {
        name: 'TO_MERGE',
        displayName: 'To Merge',
        index: 1,
    },
    ASSIGNED: {
        name: 'ASSIGNED',
        displayName: 'Assigned',
        index: 2,
    },
    UNASSIGNED: {
        name: 'UNASSIGNED',
        displayName: 'Unassigned',
        index: 3,
    },
};

export type MergeEventGroupEnumType = keyof typeof MergeEventGroupEnum;

type PrimaryEventStatus = { event: MergeEventView | TileEvent; wasCleared: boolean };

export class ManageEventMergeStore {
    private readonly store: ListStore<MergeEventView>;

    @observable
    public readonly data;

    @observable
    public readonly selectedItems;

    @observable
    public currentPrimaryEvent: PrimaryEventStatus;

    @observable
    public initiallyMergedItems: MergeEventView[];

    public initiallyMergedItemIds: number[];

    private initialPrimaryEventId: number;

    constructor(
        agencyEvents: MergeEventView[],
        alreadyMergedEvents: MergeEventView[],
        parentAgencyEvent: MergeEventView
    ) {
        this.store = new ListStore<MergeEventView>();
        this.currentPrimaryEvent = { event: parentAgencyEvent, wasCleared: false };
        this.initialPrimaryEventId = parentAgencyEvent.id;

        this.store.setItems(agencyEvents);
        this.store.setSelectedItems(alreadyMergedEvents.map((event) => event.id));

        this.data = this.store.data;
        this.selectedItems = this.store.selectedItems;
        this.initiallyMergedItems = alreadyMergedEvents;
        this.initiallyMergedItemIds = this.initiallyMergedItems.map((i) => i.id);

        this.store.setGroupingDirective(this.getMergeStoreGroupingDirective());

        makeObservable(this);
    }

    @action
    public updatePrimaryEvent(newPrimaryEvent: MergeEventView | TileEvent): void {
        this.currentPrimaryEvent = { event: newPrimaryEvent, wasCleared: false };

        // Ensure the new primary event is selected
        if (!this.store.selectedItems[newPrimaryEvent.id]) {
            this.store.toggleItemSelection(newPrimaryEvent.id);
        }

        this.store.setGroupingDirective(this.getMergeStoreGroupingDirective());
    }

    @action
    public toggleItemSelection(id: number): void {
        const currentPrimaryEventId = this.currentPrimaryEvent?.event.id;
        const userIsTogglingReactivatedPrimaryEvent =
            this.currentPrimaryEvent?.wasCleared && id === currentPrimaryEventId;

        // User may select a primary event that was reactivated
        if (id !== currentPrimaryEventId || userIsTogglingReactivatedPrimaryEvent) {
            this.store.toggleItemSelection(id);
            this.store.setGroupingDirective(this.getMergeStoreGroupingDirective());
        }
    }

    @action
    public removeItems(itemIds: number[]): void {
        this.store.removeItems(itemIds);

        const isClearingPrimaryEvent = itemIds.includes(this.currentPrimaryEvent.event.id);

        if (isClearingPrimaryEvent) {
            this.currentPrimaryEvent = { ...this.currentPrimaryEvent, wasCleared: true };
        }
    }

    @action
    public updateSearch(searchTerm: string): void {
        this.store.setFilterDirective(
            searchTerm !== ''
                ? (item: MergeEventView) => {
                      return (
                          this.selectedItems[item.id] ||
                          (item.searchString?.includes(searchTerm.toLowerCase()) ?? false)
                      );
                  }
                : undefined
        );
    }

    public getSelectedItems(): MergeEventView[] {
        return Object.keys(this.store.selectedItems)
            .map((i) => {
                const item = this.store.getItemById(parseInt(i, 10));
                if (item) {
                    return item;
                } else {
                    logWarn('Merge Event: Unexpectedly could not find item by id');
                    return undefined;
                }
            })
            .filter(isNotNullOrUndefined);
    }

    public getSelectedItemIds(): number[] {
        return this.getSelectedItems().map((item) => item.id);
    }

    public getUnselectedItems(): MergeEventView[] {
        return this.store.data.list.filter((e) => !this.selectedItems[e.id]);
    }

    public getUnmergeItems(): MergeEventView[] {
        return this.initiallyMergedItems.filter((i) => {
            return !this.getSelectedItemIds().includes(i.id);
        });
    }

    public getMergeItems(): MergeEventView[] {
        return this.getSelectedItems().filter((i) => {
            return !this.initiallyMergedItemIds.includes(i.id);
        });
    }

    /**
     * When submitting the list of merged events to our API endpoint,
     * we need to clean up the data
     */
    public getMergeItemsForApi(): MergeEventView[] {
        const primaryEventIsAlreadyMerged = this.initiallyMergedItems.find(
            (agencyEvent) => agencyEvent.id === this.currentPrimaryEvent.event.id
        );

        if (primaryEventIsAlreadyMerged) {
            return this.getMergeItems();
        } else {
            const initialPrimaryAgencyEvent = this.initiallyMergedItems.find(
                (agencyEvent) => agencyEvent.id === this.initialPrimaryEventId
            );
            return this.getMergeItems()
                .filter((i) => {
                    return i.id !== this.currentPrimaryEvent.event.id;
                })
                .concat(initialPrimaryAgencyEvent ?? []);
        }
    }

    @computed
    /**
     * This not expensive on its own, but it gets
     * called thousands of times when a new item is added,
     * so we cache the computed value
     */
    get mergedItemIds(): number[] {
        return this.getMergeItems().map((item) => item.id);
    }

    @action
    public hasItem(id: number): boolean {
        return this.store.hasItem(id);
    }

    @action
    public upsertItems(items: MergeEventView[]): void {
        this.store.upsertItems(items);

        if (
            this.currentPrimaryEvent.wasCleared &&
            items.find((i) => i.id === this.currentPrimaryEvent.event.id) !== undefined
        ) {
            this.updatePrimaryEvent(this.currentPrimaryEvent.event);
        }
    }

    @action
    public setSelectedItems(ids: number[]): void {
        this.store.setSelectedItems(ids);
    }

    public getMergeStoreGroupingDirective(): GroupingDirective<MergeEventView> {
        return {
            getGroupValue: (item: MergeEventView): MergeEventGroupEnumType => {
                if (this.itemIsAlreadyMerged(item.id)) {
                    return 'ALREADY_MERGED';
                } else if (this.mergedItemIds.includes(item.id)) {
                    return 'TO_MERGE';
                } else if (item.assignedUnits?.length > 0) {
                    return 'ASSIGNED';
                } else {
                    return 'UNASSIGNED';
                }
            },
            comparator: (left: MergeEventGroupEnumType, right: MergeEventGroupEnumType) =>
                baseGroupingComparator(left, right, (group) => MergeEventGroupEnum[group].index),
        };
    }

    public itemIsAlreadyMerged(itemId: number) {
        return this.initiallyMergedItemIds.includes(itemId);
    }

    /**
     * An item has a focusable `primary` if
     *      - it is the original parent event
     *      - it is not an already merged event
     */
    public canFocusPrimary(itemId: number) {
        if (this.initialPrimaryEventId === itemId) {
            return true;
        } else {
            return !this.itemIsAlreadyMerged(itemId);
        }
    }
}
